import React, { useState, createElement } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Sector } from 'recharts';
import { DownloadIcon, HelpCircleIcon } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { Button } from '../ui/Button';
import { formatCurrency } from '../../utils/formatUtils';
interface ExpenseChartProps {
  data: any[];
  isLoading?: boolean;
  title?: string;
  showControls?: boolean;
}
export function ExpenseChart({
  data,
  isLoading = false,
  title,
  showControls = false
}: ExpenseChartProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showTooltipHelp, setShowTooltipHelp] = useState(false);
  // If data is empty or loading, show a message
  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-400" data-id="element-276">
        <div className="animate-pulse flex flex-col items-center" data-id="element-277">
          <div className="h-8 w-8 rounded-full border-4 border-t-transparent border-gray-500 animate-spin mb-4" data-id="element-278"></div>
          <div data-id="element-279">Loading expense data...</div>
        </div>
      </div>;
  }
  if (!data || data.length === 0) {
    return <div className="flex items-center justify-center h-64 text-gray-400" data-id="element-280">
        No data available for the selected criteria
      </div>;
  }
  // Check if data has date property - if so, it's time-series data
  const isTimeSeriesData = data[0] && 'date' in data[0];
  // Colors for the charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#14b8a6', '#f43f5e', '#84cc16', '#a855f7', '#64748b' // slate
  ];
  // Custom tooltip for the charts
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg p-3 text-sm" data-id="element-281">
          {isTimeSeriesData ? <>
              <p className="text-gray-200 font-medium mb-1" data-id="element-282">{label}</p>
              <p className="text-gray-300" data-id="element-283">
                <span className="font-medium text-blue-400" data-id="element-284">Total:</span>{' '}
                {formatCurrency(payload[0].value)}
              </p>
            </> : <>
              <p className="text-gray-200 font-medium mb-1" data-id="element-285">
                {payload[0].name}
              </p>
              <p className="text-gray-300" data-id="element-286">
                <span className="font-medium text-blue-400" data-id="element-287">Amount:</span>{' '}
                {formatCurrency(payload[0].value)}
              </p>
              <p className="text-gray-300" data-id="element-288">
                <span className="font-medium text-blue-400" data-id="element-289">Percentage:</span>{' '}
                {(payload[0].payload.percent * 100).toFixed(1)}%
              </p>
            </>}
        </div>;
    }
    return null;
  };
  // Custom active shape for pie chart
  const renderActiveShape = (props: any) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value
    } = props;
    return <g data-id="element-290">
        <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 6} startAngle={startAngle} endAngle={endAngle} fill={fill} data-id="element-291" />
        <Sector cx={cx} cy={cy} startAngle={startAngle} endAngle={endAngle} innerRadius={outerRadius + 8} outerRadius={outerRadius + 10} fill={fill} data-id="element-292" />
        <text x={cx} y={cy - 15} textAnchor="middle" fill="#e5e7eb" className="text-sm font-medium" data-id="element-293">
          {payload.name}
        </text>
        <text x={cx} y={cy + 5} textAnchor="middle" fill="#e5e7eb" className="text-base font-bold" data-id="element-294">
          {formatCurrency(value)}
        </text>
        <text x={cx} y={cy + 25} textAnchor="middle" fill="#9ca3af" className="text-xs" data-id="element-295">
          {`${(percent * 100).toFixed(1)}%`}
        </text>
      </g>;
  };
  // Function to download chart data as CSV
  const downloadCSV = () => {
    // Create CSV content
    let csvContent = 'data:text/csv;charset=utf-8,';
    if (isTimeSeriesData) {
      // Headers for time series data
      csvContent += 'Date,Amount\n';
      // Data rows
      data.forEach(item => {
        csvContent += `${item.date},${item.amount}\n`;
      });
    } else {
      // Headers for category data
      csvContent += 'Category,Amount,Percentage\n';
      // Calculate total for percentage
      const total = data.reduce((sum, item) => sum + item.value, 0);
      // Data rows
      data.forEach(item => {
        const percentage = (item.value / total * 100).toFixed(2);
        csvContent += `${item.name},${item.value},${percentage}%\n`;
      });
    }
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `expense-data-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    // Trigger download
    link.click();
    document.body.removeChild(link);
  };
  if (isTimeSeriesData) {
    // Render bar chart for time series data
    return <div data-id="element-296">
        {title && <div className="flex justify-between items-center mb-4" data-id="element-297">
            <h3 className="text-lg font-medium text-gray-100" data-id="element-298">{title}</h3>
            {showControls && <div className="flex items-center gap-2" data-id="element-299">
                <Tooltip content="Download data as CSV" position="top" data-id="element-300">
                  <Button variant="outline" size="sm" onClick={downloadCSV} className="text-gray-300" data-id="element-301">
                    <DownloadIcon size={16} className="mr-1" data-id="element-302" />
                    Export
                  </Button>
                </Tooltip>
                <Tooltip content="This chart shows your expenses over time. Click on a bar to see details." position="left" data-id="element-303">
                  <div className="text-gray-400 hover:text-gray-300 cursor-help" data-id="element-304">
                    <HelpCircleIcon size={18} data-id="element-305" />
                  </div>
                </Tooltip>
              </div>}
          </div>}
        <div className="h-80" data-id="element-306">
          <ResponsiveContainer width="100%" height="100%" data-id="element-307">
            <BarChart data={data} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 60
          }} barSize={36} data-id="element-308">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} data-id="element-309" />
              <XAxis dataKey="date" tick={{
              fill: '#9ca3af',
              fontSize: 12
            }} axisLine={{
              stroke: '#4b5563'
            }} angle={-45} textAnchor="end" height={60} tickMargin={8} data-id="element-310" />
              <YAxis tick={{
              fill: '#9ca3af',
              fontSize: 12
            }} axisLine={{
              stroke: '#4b5563'
            }} tickFormatter={value => `$${value}`} tickMargin={8} data-id="element-311" />
              <RechartsTooltip content={<CustomTooltip data-id="element-313" />} cursor={{
              fill: 'rgba(107, 114, 128, 0.15)'
            }} data-id="element-312" />
              <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} animationDuration={800} animationEasing="ease-in-out" data-id="element-314" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>;
  } else {
    // Add percentage to data for the pie chart
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    const dataWithPercent = data.map(entry => ({
      ...entry,
      percent: entry.value / total
    }));
    // Render pie chart for category data
    return <div data-id="element-315">
        {title && <div className="flex justify-between items-center mb-4" data-id="element-316">
            <h3 className="text-lg font-medium text-gray-100" data-id="element-317">{title}</h3>
            {showControls && <div className="flex items-center gap-2" data-id="element-318">
                <Tooltip content="Download data as CSV" position="top" data-id="element-319">
                  <Button variant="outline" size="sm" onClick={downloadCSV} className="text-gray-300" data-id="element-320">
                    <DownloadIcon size={16} className="mr-1" data-id="element-321" />
                    Export
                  </Button>
                </Tooltip>
                <Tooltip content="This chart shows your expenses by category. Click on a segment to see details." position="left" data-id="element-322">
                  <div className="text-gray-400 hover:text-gray-300 cursor-help" data-id="element-323">
                    <HelpCircleIcon size={18} data-id="element-324" />
                  </div>
                </Tooltip>
              </div>}
          </div>}
        <div className="h-80" data-id="element-325">
          <ResponsiveContainer width="100%" height="100%" data-id="element-326">
            <PieChart data-id="element-327">
              <Pie activeIndex={activeIndex} activeShape={renderActiveShape} data={dataWithPercent} cx="50%" cy="50%" innerRadius={70} outerRadius={100} fill="#8884d8" dataKey="value" nameKey="name" onMouseEnter={(_, index) => setActiveIndex(index)} animationDuration={800} animationEasing="ease-in-out" data-id="element-328">
                {dataWithPercent.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#1f2937" strokeWidth={1} data-id="element-329" />)}
              </Pie>
              <RechartsTooltip content={<CustomTooltip data-id="element-331" />} data-id="element-330" />
              <Legend verticalAlign="bottom" height={36} formatter={(value, entry, index) => <span className="text-gray-300 text-sm" data-id="element-333">{value}</span>} onClick={data => {
              const index = dataWithPercent.findIndex(item => item.name === data.value);
              if (index >= 0) setActiveIndex(index);
            }} data-id="element-332" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>;
  }
}